import React, { useState, useEffect, useRef } from 'react';
import { Check, Info, AlertTriangle, AlertCircle, X } from 'lucide-react';

export type ToastTone = 'success' | 'info' | 'error' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
  'data-testid'?: string;
}

export interface ToastProps {
  id: string;
  tone?: ToastTone;
  message: string;
  action?: ToastAction;
  durationMs?: number;
  onDismiss: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  tone = 'info',
  message,
  action,
  durationMs = 4000,
  onDismiss
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const remainingTimeRef = useRef(durationMs);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (durationMs <= 0) return;

    if (isPaused) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
      return;
    }

    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      onDismiss(id);
    }, remainingTimeRef.current);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isPaused, id, onDismiss, durationMs]);

  const getIcon = () => {
    switch (tone) {
      case 'success':
        return (
          <span className="size-6 rounded-full bg-success/15 text-success-soft flex items-center justify-center shrink-0">
            <Check className="size-3.5 stroke-[3]" />
          </span>
        );
      case 'error':
        return (
          <span className="size-6 rounded-full bg-error/15 text-error-soft flex items-center justify-center shrink-0">
            <AlertCircle className="size-3.5 stroke-[2.5]" />
          </span>
        );
      case 'warning':
        return (
          <span className="size-6 rounded-full bg-warning/15 text-warning-soft flex items-center justify-center shrink-0">
            <AlertTriangle className="size-3.5 stroke-[2.5]" />
          </span>
        );
      case 'info':
      default:
        return (
          <span className="size-6 rounded-full bg-primary/15 text-primary-soft flex items-center justify-center shrink-0">
            <Info className="size-3.5 stroke-[2.5]" />
          </span>
        );
    }
  };

  const getBorderColor = () => {
    switch (tone) {
      case 'error':
        return 'border-error/40';
      case 'warning':
        return 'border-warning/40';
      default:
        return 'border-base-300';
    }
  };

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live="polite"
      data-testid="toast-item"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      className={`pointer-events-auto flex items-center gap-3 px-4 py-3 bg-neutral border ${getBorderColor()} rounded-2xl shadow-xl text-sm max-w-[90vw] md:max-w-md select-none transition animate-in fade-in slide-in-from-top-3 duration-200`}
    >
      {getIcon()}
      <span className="text-neutral-content font-medium flex-1 text-xs lg:text-sm">
        {message}
      </span>
      {action && (
        <button
          type="button"
          onClick={() => {
            action.onClick();
            onDismiss(id);
          }}
          data-testid={action['data-testid'] || (action.label === 'بازگرداندن' ? 'toast-undo-action' : 'toast-action')}
          className="text-primary-soft font-bold text-xs lg:text-sm hover:underline px-2 py-1 rounded transition shrink-0"
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => onDismiss(id)}
        aria-label="بستن اعلان"
        className="text-neutral-content/60 hover:text-neutral-content p-1 rounded-lg transition shrink-0"
      >
        <X className="size-4" />
      </button>
    </div>
  );
};

Toast.displayName = 'Toast';
